import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  LogOut,
  ShieldCheck,
  TrendingUp,
  Users,
  Truck,
  Heart,
  Zap,
} from "lucide-react";
import ActivityTrailMap from "../components/ActivityTrailMap";
import ForecastPanel from "../components/ForecastPanel";
import { ReplayTourButton } from "../components/OnboardingTour";
import { toast } from "sonner";

// ─────────────────────────────────────────────────────────────────────────────
// Irrefutable Operational Record Dashboard
// Frontend-only presentation layer. Zero new backend endpoints, calculations,
// collections, schema fields, or integrations.
// Every value below is read verbatim from an existing API response.
// ─────────────────────────────────────────────────────────────────────────────

const PERIODS = [
  { value: "today", label: "Today" },
  { value: "week", label: "7 Days" },
  { value: "month", label: "30 Days" },
  { value: "quarter", label: "Quarter" },
  { value: "year", label: "Year" },
];

const fmtGBP = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(Number(n || 0));

const fmtPct = (n) => `${Number(n || 0).toFixed(0)}%`;

const BAND_COLOURS = {
  green: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500", label: "Healthy" },
  amber: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-500", label: "Caution" },
  red: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", dot: "bg-rose-500", label: "Critical" },
};

const SEVERITY_COLOURS = {
  high: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", dot: "bg-rose-500" },
  medium: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-500" },
  low: { bg: "bg-slate-50", text: "text-slate-700", border: "border-slate-200", dot: "bg-slate-400" },
};

// Direction symbol for "current vs previous" comparisons
const dirArrow = (delta) => {
  if (delta === null || delta === undefined) return ArrowRight;
  if (delta > 0.05) return ArrowUp;
  if (delta < -0.05) return ArrowDown;
  return ArrowRight;
};
const dirTone = (delta) => {
  if (delta === null || delta === undefined) return "text-slate-500";
  if (delta > 0.05) return "text-emerald-600";
  if (delta < -0.05) return "text-rose-600";
  return "text-slate-500";
};

// Derive a green/amber/red band from raw percent (for non-owner roles
// who cannot call /api/client-health). Same bucket math used by
// existing ClientPortal HealthStrip — no new scoring.
const bandFromPct = (pct) => {
  if (pct === null || pct === undefined) return "amber";
  if (pct >= 75) return "green";
  if (pct >= 45) return "amber";
  return "red";
};

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

export default function OperationalRecord() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const role = user?.role;

  const [period, setPeriod] = useState("month");

  // Source-of-truth API responses (verbatim, never mutated)
  const [clientReport, setClientReport] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [workforceSummary, setWorkforceSummary] = useState(null);
  const [workforceAlerts, setWorkforceAlerts] = useState(null);
  const [clientHealth, setClientHealth] = useState(null);
  const [fleetMetrics, setFleetMetrics] = useState(null);
  const [platformOps, setPlatformOps] = useState(null);
  const [fundraiserRecords, setFundraiserRecords] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Period-mapping for endpoints that use the 5-window slider
  // (today / week / month / quarter / year — defined in _client_period_window)
  const reportPeriod = period;

  // Fetch everything in parallel. Each request silently no-ops on 401/403
  // so role-gated sections simply do not render — no error toast.
  const refresh = useCallback(async () => {
    setLoading(true);

    const safe = async (req) => {
      try {
        const res = await req();
        return res?.data ?? null;
      } catch (err) {
        return null;
      }
    };

    const fetches = [];

    // Roles that can call /client/report: owner, platform_owner, manager, client
    if (["owner", "platform_owner", "manager", "client"].includes(role)) {
      fetches.push(safe(() => apiClient.get(`/client/report?period=${reportPeriod}`)).then((d) => setClientReport(d)));
    } else {
      setClientReport(null);
    }

    // /forecast — same role set + management-subtree fundraisers
    fetches.push(safe(() => apiClient.get(`/forecast`)).then((d) => setForecast(d)));

    // /workforce/summary + /workforce/alerts — owner / platform_owner / managers /
    // management-teamRole fundraisers (regional/area/national managers, team leaders, campaign managers).
    // OCI V2.16: PLAIN fundraisers excluded — org-level workforce readiness is Manager/Owner data,
    // not personal. (Backend also enforces this via _can_management; this aligns the UI intent and
    // avoids a wasted 403 request.)
    const MGMT_TEAM_ROLES = ["national_manager", "regional_manager", "area_manager", "team_leader", "campaign_manager"];
    const isManagement = ["owner", "platform_owner", "manager"].includes(role)
      || (role === "fundraiser" && MGMT_TEAM_ROLES.includes(user?.teamRole));
    if (isManagement) {
      fetches.push(safe(() => apiClient.get(`/workforce/summary`)).then((d) => setWorkforceSummary(d)));
      fetches.push(safe(() => apiClient.get(`/workforce/alerts`)).then((d) => setWorkforceAlerts(d)));
    }

    // /client-health — owner + platform_owner only
    if (["owner", "platform_owner"].includes(role)) {
      fetches.push(safe(() => apiClient.get(`/client-health`)).then((d) => setClientHealth(d)));
    }

    // Platform-only surfaces
    if (role === "platform_owner") {
      fetches.push(safe(() => apiClient.get(`/platform/fleet-metrics`)).then((d) => setFleetMetrics(d)));
      fetches.push(safe(() => apiClient.get(`/platform/operations`)).then((d) => setPlatformOps(d)));
    }

    // Fundraiser personal record
    if (role === "fundraiser") {
      fetches.push(safe(() => apiClient.get(`/fundraiser/records`)).then((d) => setFundraiserRecords(d)));
    }

    await Promise.allSettled(fetches);
    setLoading(false);
  }, [role, user?.teamRole, reportPeriod]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // ── Back-navigation target per role
  const homePath = useMemo(() => {
    if (role === "fundraiser") return "/dashboard";
    if (role === "owner" || role === "manager") return "/owner";
    if (role === "platform_owner") return "/platform";
    if (role === "client") return "/client";
    return "/";
  }, [role]);

  // ── Board pack export (existing endpoint)
  const exportBoardPack = async () => {
    setExporting(true);
    try {
      const resp = await apiClient.get(`/board-pack/export?period=${reportPeriod === "year" ? "month" : (reportPeriod === "quarter" ? "month" : reportPeriod === "today" ? "week" : reportPeriod)}`, {
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "text/html" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `operational-record-board-pack-${new Date().toISOString().slice(0, 10)}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Board Pack downloaded");
    } catch (e) {
      toast.error("Board Pack export failed");
    } finally {
      setExporting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // DERIVATIONS (all from existing API responses, no new math)
  // ─────────────────────────────────────────────────────────────────────────

  // Section 1 — Operational Health
  // Owner/platform_owner: use composite from /client-health
  // Others: derive band from /client/report.kpis.deliverySuccessPct + retentionRatePct
  // Fundraiser: personal active/cancellations from /fundraiser/records
  const overallHealth = useMemo(() => {
    if (clientHealth?.band) {
      return {
        band: clientHealth.band,
        score: clientHealth.score,
        narrative: `${clientHealth.score}/100`,
        urgentActions: clientHealth.urgentActions || [],
        strengths: clientHealth.strengths || [],
        risks: clientHealth.risks || [],
      };
    }
    if (clientReport?.kpis) {
      const delivery = Number(clientReport.kpis.deliverySuccessPct || 0);
      const retention = Number(clientReport.kpis.retentionRatePct || 0);
      const blended = (delivery + retention) / 2;
      return { band: bandFromPct(blended), score: Math.round(blended), narrative: `${Math.round(blended)}/100 (delivery + retention)`, urgentActions: [], strengths: [], risks: [] };
    }
    if (role === "fundraiser" && fundraiserRecords?.summary) {
      const s = fundraiserRecords.summary;
      const total = (s.activeSignups || 0) + (s.cancellations || 0);
      const rate = total ? (s.activeSignups / total) * 100 : 0;
      return { band: bandFromPct(rate), score: Math.round(rate), narrative: `${Math.round(rate)}/100 personal retention`, urgentActions: [], strengths: [], risks: [] };
    }
    return null;
  }, [clientHealth, clientReport, fundraiserRecords, role]);

  // Section 1 — sub-axes (Delivery / Retention / Activity / Coverage)
  const healthAxes = useMemo(() => {
    if (!clientReport?.kpis) return null;
    const k = clientReport.kpis;
    return {
      delivery: { pct: Number(k.deliverySuccessPct || 0), band: bandFromPct(Number(k.deliverySuccessPct || 0)) },
      retention: { pct: Number(k.retentionRatePct || 0), band: bandFromPct(Number(k.retentionRatePct || 0)) },
      activity: { pct: Math.min(100, (k.signups || 0) ? 100 : 0), band: (k.signups || 0) > 0 ? "green" : "amber", count: k.signups },
      coverage: { pct: 0, band: "amber", count: k.activeCampaigns || 0, label: `${k.activeCampaigns || 0} active campaigns` },
    };
  }, [clientReport]);

  // Section 2 — Pulse (period vs deltas)
  // Use the dailyTrend from client/report to derive current vs prior half-period.
  const pulseTiles = useMemo(() => {
    if (!clientReport) return null;
    const trend = Array.isArray(clientReport.dailyTrend) ? clientReport.dailyTrend : [];
    const n = trend.length;
    const half = Math.floor(n / 2) || 1;
    const recent = trend.slice(n - half);
    const prior = trend.slice(0, n - half);
    const sum = (arr, key) => arr.reduce((a, b) => a + Number(b?.[key] || 0), 0);
    const recentConv = sum(recent, "signups");
    const priorConv = sum(prior, "signups");
    const recentRev = sum(recent, "revenue");
    const priorRev = sum(prior, "revenue");
    const conversionDelta = priorConv ? (recentConv - priorConv) / priorConv : null;
    const revenueDelta = priorRev ? (recentRev - priorRev) / priorRev : null;

    const cancellations = clientReport.kpis?.cancellations || 0;
    const priorCancellations = null; // single-period only — show absolute

    return {
      conversions: {
        current: clientReport.kpis?.signups ?? clientReport.kpis?.conversions ?? 0,
        previous: priorConv,
        delta: conversionDelta,
      },
      revenue: {
        current: Number(clientReport.kpis?.totalRevenue ?? clientReport.kpis?.estRevenue ?? 0),
        previous: priorRev,
        delta: revenueDelta,
      },
      locations: {
        current: clientReport.kpis?.activeCampaigns || 0, // active campaigns proxy
        previous: null,
        delta: null,
      },
      cancellations: {
        current: cancellations,
        previous: priorCancellations,
        delta: null,
      },
      momentum: typeof forecast?.momentum === "number"
        ? (forecast.momentum > 1.05 ? "accelerating" : forecast.momentum < 0.85 ? "cooling" : "stable")
        : (forecast?.momentum?.label || "stable"),
    };
  }, [clientReport, forecast]);

  // Section 3 — Lifecycle (verbatim from clientReport.lifecycle)
  const lifecycle = clientReport?.lifecycle || null;

  // Section 6 — Workforce Readiness (verbatim from workforceSummary)
  // Section 7 — Executive Intelligence (deterministic rule set)
  const executiveAlerts = useMemo(() => {
    const alerts = [];
    // Schedule shortfall
    if (forecast?.scheduleGap?.status === "shortfall") {
      const g = forecast.scheduleGap;
      alerts.push({
        id: "schedule_shortfall",
        severity: "high",
        title: "Schedule shortfall detected",
        body: `Needed ${g.needed || 0} fundraiser-days · Scheduled ${g.scheduled || 0}.`,
      });
    }
    // Delivery failures (last 24h via lastFailureAt)
    if (clientReport?.delivery) {
      const d = clientReport.delivery;
      const lastFail = d.lastFailureAt ? new Date(d.lastFailureAt) : null;
      const within24h = lastFail && (Date.now() - lastFail.getTime() <= 24 * 60 * 60 * 1000);
      if ((d.failed || 0) > 0 && within24h) {
        alerts.push({
          id: "delivery_failures",
          severity: "high",
          title: "Delivery failure in the last 24 hours",
          body: `${d.failed} record${d.failed === 1 ? "" : "s"} failed delivery · last failure ${lastFail.toLocaleString()}.`,
        });
      }
    }
    // Rising cancellations: pulse-derived (current vs prior half-period × 1.5)
    if (pulseTiles?.cancellations?.current && clientReport?.dailyTrend?.length) {
      const trend = clientReport.dailyTrend;
      const n = trend.length;
      const half = Math.floor(n / 2) || 1;
      const recent = trend.slice(n - half);
      const prior = trend.slice(0, n - half);
      const recentCancel = recent.reduce((a, b) => a + Number(b?.cancellations || 0), 0);
      const priorCancel = prior.reduce((a, b) => a + Number(b?.cancellations || 0), 0);
      if (priorCancel > 0 && recentCancel > priorCancel * 1.5) {
        alerts.push({
          id: "rising_cancellations",
          severity: "medium",
          title: "Cancellations rising sharply",
          body: `${recentCancel} recent · ${priorCancel} prior · +${Math.round((recentCancel / priorCancel - 1) * 100)}%.`,
        });
      }
    }
    // Forecast deteriorating
    if (forecast?.momentum?.label === "cooling" && forecast?.confidence !== "low") {
      alerts.push({
        id: "forecast_cooling",
        severity: "medium",
        title: "Forecast momentum is cooling",
        body: `7-day momentum is cooling with ${forecast.confidence || "medium"} confidence — review schedule and coverage.`,
      });
    }
    // Compliance expiring
    if (workforceSummary) {
      if ((workforceSummary.expired || 0) > 0) {
        alerts.push({
          id: "rtw_expired",
          severity: "high",
          title: `${workforceSummary.expired} Right-to-Work record${workforceSummary.expired === 1 ? "" : "s"} expired`,
          body: "Review Workforce → Compliance immediately.",
        });
      }
      if ((workforceSummary.expiringSoon30d || 0) > 0) {
        alerts.push({
          id: "rtw_expiring_soon",
          severity: "medium",
          title: `${workforceSummary.expiringSoon30d} Right-to-Work record${workforceSummary.expiringSoon30d === 1 ? "" : "s"} expiring within 30 days`,
          body: "Schedule renewal checks this week.",
        });
      }
      if ((workforceSummary.missingBank || 0) > 0 && ["owner", "platform_owner"].includes(role)) {
        alerts.push({
          id: "missing_bank",
          severity: "medium",
          title: `${workforceSummary.missingBank} staff with no bank details`,
          body: "Required before payroll can be processed.",
        });
      }
    }
    // Platform owner only — email outcomes from /platform/operations.supportIssues (high)
    if (role === "platform_owner" && platformOps?.supportIssues) {
      const high = platformOps.supportIssues.filter((i) => i?.severity === "high");
      if (high.length > 0) {
        alerts.push({
          id: "fleet_support_high",
          severity: "high",
          title: `${high.length} high-severity fleet issue${high.length === 1 ? "" : "s"}`,
          body: high
            .slice(0, 3)
            .map((i) => i.title || i.type)
            .filter(Boolean)
            .join(" · ") || "Review Platform → Support Issues.",
        });
      }
    }
    // Sort by severity (high > medium > low)
    const rank = { high: 0, medium: 1, low: 2 };
    return alerts.sort((a, b) => (rank[a.severity] ?? 3) - (rank[b.severity] ?? 3));
  }, [forecast, clientReport, pulseTiles, workforceSummary, platformOps, role]);

  // Section 8 — Board-Level Summary lines (deterministic templates)
  const boardSummaryLines = useMemo(() => {
    const lines = [];
    if (overallHealth) {
      lines.push(`Operational health is ${overallHealth.band}.`);
    }
    if (clientReport?.kpis && pulseTiles) {
      const sign = pulseTiles.conversions.delta === null
        ? ""
        : (pulseTiles.conversions.delta > 0.05 ? "up " : pulseTiles.conversions.delta < -0.05 ? "down " : "flat ");
      const pct = pulseTiles.conversions.delta === null
        ? ""
        : `${Math.abs(Math.round(pulseTiles.conversions.delta * 100))}% `;
      lines.push(
        `${clientReport.kpis.signups ?? clientReport.kpis.conversions ?? 0} signups in the last ${PERIODS.find((p) => p.value === reportPeriod)?.label.toLowerCase() || "30 days"}${pulseTiles.conversions.delta !== null ? ` (${sign}${pct}vs prior).` : "."}`
      );
    }
    if (forecast?.next7 !== undefined) {
      const next7Count = typeof forecast.next7 === "number"
        ? forecast.next7
        : (forecast.next7?.expectedConversions ?? 0);
      const momentumLabel = typeof forecast.momentum === "number"
        ? (forecast.momentum > 1.05 ? "accelerating" : forecast.momentum < 0.85 ? "cooling" : "stable")
        : (forecast.momentum?.label || "stable");
      lines.push(
        `Forecast for the next 7 days: ${Math.round(next7Count || 0)} signups (confidence ${forecast.confidence || "medium"}, momentum ${momentumLabel}).`
      );
    }
    if (clientReport?.delivery) {
      const d = clientReport.delivery;
      const total = (d.delivered || 0) + (d.failed || 0) + (d.pending || 0);
      lines.push(
        `Delivery success rate is ${fmtPct(d.successPct)} (${d.delivered || 0} of ${total} records).`
      );
    }
    if (workforceSummary) {
      const need = (workforceSummary.expiringSoon30d || 0) + (workforceSummary.expired || 0);
      if (need > 0) {
        lines.push(`${need} compliance item${need === 1 ? "" : "s"} need attention.`);
      }
    }
    return lines.slice(0, 5);
  }, [overallHealth, clientReport, pulseTiles, forecast, workforceSummary, reportPeriod]);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="op-record-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(homePath)}
              className="h-9 px-2 text-slate-500"
              data-testid="op-record-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-700">
                Operational Record
              </div>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900" data-testid="op-record-title">
                {role === "platform_owner"
                  ? "Fleet & operational record"
                  : role === "client"
                  ? "Shared operational record"
                  : role === "fundraiser"
                  ? "Your operational record"
                  : "Operational record"}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500 hidden md:block">{user?.email}</span>
            <ReplayTourButton />
            <Button variant="outline" size="sm" onClick={logout} data-testid="op-record-logout-btn">
              <LogOut className="h-4 w-4 mr-1.5" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Period selector */}
        <div className="flex flex-wrap items-center justify-between gap-3" data-testid="op-record-period-tabs">
          <div className="flex flex-wrap items-center gap-1">
            {PERIODS.map((p) => (
              <Button
                key={p.value}
                size="sm"
                variant={period === p.value ? "default" : "outline"}
                onClick={() => setPeriod(p.value)}
                className="h-8 px-3 text-xs"
                data-testid={`op-record-period-${p.value}`}
              >
                {p.label}
              </Button>
            ))}
          </div>
          {(role === "owner" || role === "platform_owner" || role === "client" || role === "manager") && (
            <Button
              variant="default"
              size="sm"
              onClick={exportBoardPack}
              disabled={exporting}
              className="h-8 px-3 text-xs"
              data-testid="op-record-board-pack-btn"
            >
              <Download className="h-4 w-4 mr-1.5" />
              {exporting ? "Exporting…" : "Export Board Pack"}
            </Button>
          )}
        </div>

        {/* ── Section 1: Operational Health ────────────────────────────── */}
        <Card data-testid="op-record-health-strip">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Heart className="h-4 w-4 text-emerald-600" />
              Section 1 · Operational Health
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">Is the operation healthy?</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {overallHealth ? (
              <div className={`p-4 rounded-lg border ${BAND_COLOURS[overallHealth.band].bg} ${BAND_COLOURS[overallHealth.band].border}`}>
                <div className="flex items-center gap-3">
                  <span className={`inline-block w-3 h-3 rounded-full ${BAND_COLOURS[overallHealth.band].dot}`} />
                  <span
                    className={`text-sm font-semibold uppercase tracking-wide ${BAND_COLOURS[overallHealth.band].text}`}
                    data-testid="op-record-health-overall-band"
                  >
                    {BAND_COLOURS[overallHealth.band].label}
                  </span>
                  <span className="text-sm text-slate-600">· {overallHealth.narrative}</span>
                </div>
                {(overallHealth.urgentActions || []).length > 0 && (
                  <ul className="mt-3 space-y-1">
                    {overallHealth.urgentActions.slice(0, 3).map((u, i) => (
                      <li key={i} className="text-xs text-slate-700 flex items-start gap-1.5" data-testid={`op-record-health-urgent-${i + 1}`}>
                        <AlertTriangle className="h-3 w-3 mt-0.5 text-rose-500 flex-shrink-0" />
                        <span>{u}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <p className="text-xs text-slate-500">{loading ? "Loading health…" : "No data captured for this window yet."}</p>
            )}

            {healthAxes && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { id: "delivery", icon: Truck, label: "Delivery", value: fmtPct(healthAxes.delivery.pct), band: healthAxes.delivery.band },
                  { id: "retention", icon: Heart, label: "Retention", value: fmtPct(healthAxes.retention.pct), band: healthAxes.retention.band },
                  { id: "activity", icon: Activity, label: "Activity", value: (clientReport?.kpis?.signups || 0), band: healthAxes.activity.band },
                  { id: "coverage", icon: ShieldCheck, label: "Coverage", value: healthAxes.coverage.label, band: healthAxes.coverage.band },
                ].map((ax) => (
                  <div
                    key={ax.id}
                    className={`p-3 rounded-md border ${BAND_COLOURS[ax.band].border} bg-white`}
                    data-testid={`op-record-health-${ax.id}`}
                  >
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <ax.icon className="h-3.5 w-3.5 text-slate-500" />
                      <span className="text-[11px] font-mono uppercase tracking-wide text-slate-500">{ax.label}</span>
                      <span className={`ml-auto inline-block w-2 h-2 rounded-full ${BAND_COLOURS[ax.band].dot}`} />
                    </div>
                    <div className="text-sm font-semibold text-slate-900">{ax.value}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Section 2: Operational Pulse ─────────────────────────────── */}
        <Card data-testid="op-record-pulse">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-indigo-600" />
              Section 2 · Operational Pulse
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">What changed recently?</p>
          </CardHeader>
          <CardContent>
            {pulseTiles ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  {
                    id: "conversions",
                    label: "Conversions",
                    current: pulseTiles.conversions.current,
                    previous: pulseTiles.conversions.previous,
                    delta: pulseTiles.conversions.delta,
                  },
                  {
                    id: "revenue",
                    label: "Revenue",
                    current: fmtGBP(pulseTiles.revenue.current),
                    previous: fmtGBP(pulseTiles.revenue.previous),
                    delta: pulseTiles.revenue.delta,
                  },
                  {
                    id: "locations",
                    label: "Active Campaigns",
                    current: pulseTiles.locations.current,
                    previous: pulseTiles.locations.previous,
                    delta: pulseTiles.locations.delta,
                  },
                  {
                    id: "cancellations",
                    label: "Cancellations",
                    current: pulseTiles.cancellations.current,
                    previous: pulseTiles.cancellations.previous,
                    delta: pulseTiles.cancellations.delta,
                  },
                ].map((t) => {
                  const Dir = dirArrow(t.delta);
                  return (
                    <div key={t.id} className="p-3 rounded-md border border-slate-200 bg-white" data-testid={`op-record-pulse-${t.id}`}>
                      <div className="text-[11px] font-mono uppercase tracking-wide text-slate-500 mb-1.5">{t.label}</div>
                      <div className="text-xl font-semibold text-slate-900">{t.current}</div>
                      <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                        <Dir className={`h-3 w-3 ${dirTone(t.delta)}`} />
                        {t.previous !== null && t.previous !== undefined ? (
                          <>
                            <span>prior {typeof t.previous === "number" && t.id !== "revenue" ? Math.round(t.previous) : t.previous}</span>
                          </>
                        ) : (
                          <span>vs prior: n/a</span>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div className="col-span-2 lg:col-span-4 mt-1 flex items-center gap-2">
                  <Badge variant="outline" data-testid="op-record-pulse-momentum">
                    Momentum · {pulseTiles.momentum}
                  </Badge>
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-500">{loading ? "Loading pulse…" : "No data captured for this window yet."}</p>
            )}
          </CardContent>
        </Card>

        {/* ── Section 3: Lifecycle Status ──────────────────────────────── */}
        <Card data-testid="op-record-lifecycle-bar">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Activity className="h-4 w-4 text-violet-600" />
              Section 3 · Lifecycle Status
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">Where is work flowing?</p>
          </CardHeader>
          <CardContent>
            {lifecycle ? (
              <div className="grid grid-cols-5 gap-2">
                {[
                  { id: "conversations", label: "Conversations", value: lifecycle.conversations },
                  { id: "signups", label: "Signups", value: lifecycle.signups },
                  { id: "delivered", label: "Delivered", value: lifecycle.delivered },
                  { id: "active", label: "Active", value: lifecycle.active },
                  { id: "cancelled", label: "Cancelled", value: lifecycle.cancelled },
                ].map((s) => (
                  <div key={s.id} className="p-3 rounded-md border border-slate-200 bg-white text-center" data-testid={`op-record-lifecycle-stage-${s.id}`}>
                    <div className="text-[10px] font-mono uppercase tracking-wide text-slate-500 mb-1">{s.label}</div>
                    <div className="text-base font-semibold text-slate-900">{s.value || 0}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-500">{loading ? "Loading lifecycle…" : "No data captured for this window yet."}</p>
            )}
          </CardContent>
        </Card>

        {/* ── Section 4: Forecast Window ───────────────────────────────── */}
        {(role === "owner" || role === "platform_owner" || role === "manager" || role === "client") ? (
          <div data-testid="op-record-forecast">
            <ForecastPanel />
          </div>
        ) : role === "fundraiser" ? (
          <Card data-testid="op-record-forecast-personal">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Zap className="h-4 w-4 text-amber-600" />
                Section 4 · Personal Pace
              </CardTitle>
              <p className="text-xs text-slate-500 mt-1">What is likely to happen next for you?</p>
            </CardHeader>
            <CardContent>
              {fundraiserRecords?.summary ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3 rounded-md border border-slate-200" data-testid="op-record-forecast-personal-signups30">
                    <div className="text-[11px] font-mono uppercase tracking-wide text-slate-500">Last 30 days</div>
                    <div className="text-lg font-semibold text-slate-900">{fundraiserRecords.summary.last30dSignups || 0} signups</div>
                  </div>
                  <div className="p-3 rounded-md border border-slate-200">
                    <div className="text-[11px] font-mono uppercase tracking-wide text-slate-500">Active</div>
                    <div className="text-lg font-semibold text-slate-900">{fundraiserRecords.summary.activeSignups || 0}</div>
                  </div>
                  <div className="p-3 rounded-md border border-slate-200">
                    <div className="text-[11px] font-mono uppercase tracking-wide text-slate-500">Cancellations</div>
                    <div className="text-lg font-semibold text-slate-900">{fundraiserRecords.summary.cancellations || 0}</div>
                  </div>
                  <div className="p-3 rounded-md border border-slate-200">
                    <div className="text-[11px] font-mono uppercase tracking-wide text-slate-500">Top road</div>
                    <div className="text-sm font-semibold text-slate-900">{fundraiserRecords.summary.topRoad || "—"}</div>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-slate-500">{loading ? "Loading personal pace…" : "No personal data captured yet."}</p>
              )}
            </CardContent>
          </Card>
        ) : null}

        {/* ── Section 5: Coverage & Activity ──────────────────────────── */}
        {(role === "owner" || role === "platform_owner" || role === "manager" || role === "client") && (
          <div data-testid="op-record-coverage-map">
            <ActivityTrailMap
              defaultMode="heat"
              hideTrailMode={role === "client" || role === "fundraiser"}
              fundraisers={[]}
            />
          </div>
        )}

        {/* ── Section 6: Workforce Readiness ──────────────────────────── */}
        {workforceSummary && (
          <Card data-testid="op-record-workforce-readiness">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Users className="h-4 w-4 text-slate-700" />
                Section 6 · Workforce Readiness
              </CardTitle>
              <p className="text-xs text-slate-500 mt-1">Can we deliver?</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="p-3 rounded-md border border-slate-200" data-testid="op-record-workforce-active">
                  <div className="text-[11px] font-mono uppercase tracking-wide text-slate-500">Active Staff</div>
                  <div className="text-xl font-semibold text-slate-900">{workforceSummary.active || 0}</div>
                  <div className="text-[11px] text-slate-500 mt-1">of {workforceSummary.total || 0} total</div>
                </div>
                <div className="p-3 rounded-md border border-slate-200" data-testid="op-record-workforce-compliance-pct">
                  <div className="text-[11px] font-mono uppercase tracking-wide text-slate-500">Compliance</div>
                  <div className="text-xl font-semibold text-slate-900">{fmtPct(workforceSummary.compliancePct)}</div>
                  <div className="text-[11px] text-slate-500 mt-1">RTW verified</div>
                </div>
                <div className="p-3 rounded-md border border-slate-200" data-testid="op-record-workforce-expiring">
                  <div className="text-[11px] font-mono uppercase tracking-wide text-slate-500">Expiring (30d)</div>
                  <div className={`text-xl font-semibold ${(workforceSummary.expiringSoon30d || 0) > 0 ? "text-amber-700" : "text-slate-900"}`}>
                    {workforceSummary.expiringSoon30d || 0}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">documents</div>
                </div>
                <div className="p-3 rounded-md border border-slate-200" data-testid="op-record-workforce-expired">
                  <div className="text-[11px] font-mono uppercase tracking-wide text-slate-500">Expired</div>
                  <div className={`text-xl font-semibold ${(workforceSummary.expired || 0) > 0 ? "text-rose-700" : "text-slate-900"}`}>
                    {workforceSummary.expired || 0}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">documents</div>
                </div>
              </div>
              {(workforceSummary.missingBank > 0 || workforceSummary.incompleteOnboarding > 0) && (
                <div className="mt-3 text-xs text-slate-600" data-testid="op-record-workforce-gaps">
                  <span className="font-mono text-[11px] uppercase tracking-wide text-slate-500 mr-2">Missing information</span>
                  {workforceSummary.missingBank > 0 && (
                    <Badge variant="outline" className="mr-2">{workforceSummary.missingBank} no bank details</Badge>
                  )}
                  {workforceSummary.incompleteOnboarding > 0 && (
                    <Badge variant="outline">{workforceSummary.incompleteOnboarding} incomplete onboarding</Badge>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ── Section 7: Executive Intelligence ───────────────────────── */}
        <Card data-testid="op-record-exec-intel">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              Section 7 · Attention Needed
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">What needs attention?</p>
          </CardHeader>
          <CardContent>
            {executiveAlerts.length === 0 ? (
              <div className="flex items-center gap-2 text-sm text-slate-600 p-3 rounded-md bg-emerald-50 border border-emerald-200" data-testid="op-record-exec-clear">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <span>Nothing flagged. The operational record is clear.</span>
              </div>
            ) : (
              <ul className="space-y-2">
                {executiveAlerts.map((a) => {
                  const c = SEVERITY_COLOURS[a.severity] || SEVERITY_COLOURS.low;
                  return (
                    <li
                      key={a.id}
                      className={`p-3 rounded-md border ${c.border} ${c.bg}`}
                      data-testid={`op-record-exec-alert-${a.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`inline-block w-2 h-2 rounded-full ${c.dot}`} />
                        <span className={`text-[10px] font-mono uppercase tracking-wide ${c.text}`}>{a.severity}</span>
                        <span className={`text-sm font-semibold ${c.text}`}>{a.title}</span>
                      </div>
                      {a.body && <p className="text-xs text-slate-700 mt-1.5">{a.body}</p>}
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* ── Section 8: Board-Level Summary ──────────────────────────── */}
        <Card data-testid="op-record-board-summary">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4 text-slate-700" />
              Section 8 · Board-Level Summary
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">How would you explain the operation in 30 seconds?</p>
          </CardHeader>
          <CardContent>
            {boardSummaryLines.length === 0 ? (
              <p className="text-xs text-slate-500">{loading ? "Composing summary…" : "Insufficient data to compose a summary."}</p>
            ) : (
              <ol className="list-decimal pl-5 space-y-1.5">
                {boardSummaryLines.map((line, i) => (
                  <li key={i} className="text-sm text-slate-800" data-testid={`op-record-board-summary-line-${i + 1}`}>
                    {line}
                  </li>
                ))}
              </ol>
            )}
            {(role === "owner" || role === "platform_owner" || role === "client") && (
              <Button
                variant="default"
                size="sm"
                onClick={exportBoardPack}
                disabled={exporting}
                className="mt-4"
                data-testid="op-record-board-summary-export"
              >
                <Download className="h-4 w-4 mr-1.5" />
                {exporting ? "Exporting…" : "Export Board Pack"}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Provenance footer */}
        <div className="text-[10px] font-mono uppercase tracking-wide text-slate-400 text-center py-4" data-testid="op-record-provenance">
          Every figure on this page is read verbatim from existing API responses. No synthetic values, no inferred values, no AI summaries.
        </div>
      </main>
    </div>
  );
}
